// Purchase Manager Dashboard — the page is only the coach role's Purchase
// panel (PurchasePanel), fed with every purchase request the purchase
// manager can see, under a slim greeting + Logout bar (no sidebar).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import './PurchaseManagerHome.css';
import PurchasePanel from './purchase/PurchasePanel';
import { getUserBalagruhas, getAllPurchaseRequests } from '../../api';
import { useRBAC } from '../../contexts/RBACContext';
import { useAuth } from '../../contexts/AuthContext';
import RoleSwitcher from '../RoleSwitcher';
import showToast from '../../utils/toast';

function PurchaseManagerDashboard() {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { hasPermission, isLoading: rbacLoading, permissions } = useRBAC();
    const userName = localStorage.getItem('name') || 'Purchase Manager';

    const [balagruhas, setBalagruhas] = useState([]);
    const [purchaseData, setPurchaseData] = useState([]);

    const permissionsLoaded = Object.keys(permissions || {}).length > 0;
    const can = (moduleName, actionName = 'Read') => {
        if (rbacLoading || !permissionsLoaded) return false;
        if (actionName.toLowerCase() === 'read') {
            return hasPermission(moduleName, 'Read') || hasPermission(moduleName, 'Manage');
        }
        return hasPermission(moduleName, actionName);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const fetchBalagruhas = async () => {
        try {
            const response = await getUserBalagruhas();
            const list = Array.isArray(response?.data) ? response.data : [];
            setBalagruhas(list.filter((b) => b._id !== 'STOCK'));
        } catch (error) {
            showToast('Error fetching balagruha', 'error');
            setBalagruhas([]);
        }
    };

    const fetchPurchases = async () => {
        try {
            const response = await getAllPurchaseRequests({ limit: 1000 });
            const requests = response?.success ? response.data?.requests || [] : [];
            setPurchaseData([...requests].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } catch (error) {
            setPurchaseData([]);
        }
    };

    useEffect(() => {
        fetchBalagruhas();
        fetchPurchases();
    }, []);

    return (
        <div className="pm-home w-full">
            <header className="pm-home-topbar">
                <div className="pm-home-profile">
                    <div className="pm-home-avatar" aria-hidden="true">{userName.charAt(0).toUpperCase()}</div>
                    <div>
                        <div className="pm-home-name">Hi {userName},</div>
                        {/* Multi-role users get a role dropdown in place of the caption. */}
                        <RoleSwitcher variant="label" fallback={<div className="pm-home-role">Purchase Manager</div>} />
                    </div>
                </div>
                <button type="button" className="pm-home-logout" onClick={handleLogout} title="Logout">
                    <LogOut size={18} strokeWidth={2} />
                    <span>Logout</span>
                </button>
            </header>

            <div className="pm-home-panel">
                <PurchasePanel
                    balagruhas={balagruhas}
                    purchaseData={purchaseData}
                    canCreate={can('Purchase Management', 'Create')}
                    canManageShop={can('Shop Management', 'Manage')}
                    onRequestCreated={fetchPurchases}
                    productApprovalMode="pm"
                />
            </div>
        </div>
    );
}

export default PurchaseManagerDashboard;
